import { useIsAdmin } from '@/hooks/use-scopes';
const GROUP_LABEL: Record<string, string> = { shared: 'Every piece', video: 'Video, reels and photo', design: 'Design: statics, carousels, stories' };
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ShieldCheck, Check } from 'lucide-react';
import { toast } from 'sonner';
import { Card } from '@/components/ui/Card';
import Button from '@/components/ui/Button';
import {
  fetchClientRouting, saveClientRouting, fetchCreativeRouting, saveCreativeRouting, type ApproverRouting,
} from '@/lib/queries';
import { useCan } from '@/hooks/use-scopes';
import { cn } from '@/lib/cn';
import { useCollapsed, CollapseChevron } from '@/components/ui/CollapsibleCard';

const errMsg = (e: unknown) => (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? 'Could not save';

/** Who approves what. On a clinic it is the standing rule; on a creative it overrides
 *  that clinic rule for this one piece of work. Nobody ticked means anyone who qualifies. */
export default function ApproverRoutingCard({ clientId, creativeId }: { clientId?: string; creativeId?: string }) {
  const qc = useQueryClient();
  const canPlan = useCan('creatives.write');
  // who approves is set by an owner or manager (Aziz 2026-09-29); who does the work stays with planners
  const isAdmin = useIsAdmin();
  const locked = (r: ApproverRouting) => !canPlan || (r.kind === 'review' && r.role !== 'client' && !isAdmin);
  const forCreative = !!creativeId;
  const key = forCreative ? ['approver-routing', 'creative', creativeId] : ['approver-routing', 'client', clientId];
  const q = useQuery({
    queryKey: key,
    queryFn: () => (forCreative ? fetchCreativeRouting(creativeId!) : fetchClientRouting(clientId!)),
    enabled: !!(creativeId || clientId),
  });
  const [draft, setDraft] = useState<Record<string, string[]> | null>(null);
  const all = q.data ?? [];
  // the clinic's final-review switch comes as a setting row (2026-09-29)
  const finalWithAd = (all.find((r) => r.role === 'final_with_ad')?.named ?? []).length > 0;
  // who signs for the clinic: the named contacts, or the piece's own doctor (Aziz, 2026-10-04)
  const doctorSignoff = (all.find((r) => r.role === 'client_signoff')?.named ?? []).includes('doctor');
  const rows = all.filter((r) => r.kind !== 'setting');
  // Hooks run before any early return, and the panel already has its own `toggle`.
  const { open: sectionOpen, toggle: toggleSection } = useCollapsed('creative.routing.open');
  const current = (role: string) => draft?.[role] ?? rows.find((r) => r.role === role)?.named ?? [];
  const dirty = draft !== null;
  const save = useMutation({
    mutationFn: () => {
      const routing: Record<string, string[]> = {};
      for (const r of rows) routing[r.role] = current(r.role);
      return forCreative ? saveCreativeRouting(creativeId!, routing) : saveClientRouting(clientId!, routing);
    },
    onSuccess: () => {
      toast.success(forCreative ? 'Approvers set for this creative' : 'Approvers set for this clinic');
      setDraft(null); qc.invalidateQueries({ queryKey: ['approver-routing'] }); qc.invalidateQueries({ queryKey: ['approvals'] });
    },
    onError: (e) => toast.error(errMsg(e)),
  });

  const finalQ = useMutation({
    mutationFn: (on: boolean) => {
      const routing: Record<string, string[] | boolean> = {};
      for (const r of rows) routing[r.role] = current(r.role);
      routing.final_with_ad = on;
      return saveClientRouting(clientId!, routing);
    },
    onSuccess: (_r, on) => {
      toast.success(on ? 'The final review now needs the art director too' : 'The final review is now for the content creator only');
      setDraft(null); qc.invalidateQueries({ queryKey: ['approver-routing'] }); qc.invalidateQueries({ queryKey: ['approvals'] });
    },
    onError: (e) => toast.error(errMsg(e)),
  });
  // who signs for the clinic: the named contacts, or each piece's own doctor (Aziz, 2026-10-04)
  const signoffQ = useMutation({
    mutationFn: (on: boolean) => {
      const routing: Record<string, string[] | boolean | string> = {};
      for (const r of rows) routing[r.role] = current(r.role);
      routing.client_signoff = on ? 'doctor' : 'clinic';
      return saveClientRouting(clientId!, routing);
    },
    onSuccess: (_r, on) => {
      toast.success(on ? 'Each piece now goes to its own doctor for sign-off' : 'Sign-off is back with the clinic contacts');
      setDraft(null); qc.invalidateQueries({ queryKey: ['approver-routing'] }); qc.invalidateQueries({ queryKey: ['approvals'] });
    },
    onError: (e) => toast.error(errMsg(e)),
  });

  const toggle = (role: string, id: string) => {
    const cur = current(role);
    const next = cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id];
    const base: Record<string, string[]> = {};
    for (const r of rows) base[r.role] = r.role === role ? next : current(r.role);
    setDraft(base);
  };

  if (q.isLoading) return forCreative ? null : <Card className="mb-5"><div className="text-xs text-muted">Loading routing...</div></Card>;
  if (!rows.length) return null;
  // Aziz 2026-09-29: on a creative the card only listed everyone at the clinic, unticked,
  // and read like a setup nobody had done. The clinic's card is the one place to set who
  // does what; a one-off change is "Reassign" on the Work card. A creative shows this card
  // only while it carries its own exception, so that exception stays visible and clearable.
  if (forCreative && !rows.some((r) => r.inherited_from === 'creative')) return null;
  const work = rows.filter((r) => r.kind === 'work');
  const review = rows.filter((r) => r.kind !== 'work');

  return (
    <Card className="mb-5">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-1">
        <button type="button" onClick={toggleSection} aria-expanded={sectionOpen}
          className="font-medium text-muted-strong text-sm inline-flex items-center gap-2 text-left">
          <CollapseChevron open={sectionOpen} /><ShieldCheck size={14} /> Routing: who does what, who approves
        </button>
        {dirty && canPlan && (
          <div className="flex gap-2">
            <Button size="sm" variant="ghost" onClick={() => setDraft(null)}>Discard</Button>
            <Button size="sm" icon={<Check size={12} />} onClick={() => save.mutate()} loading={save.isPending}>Save</Button>
          </div>
        )}
      </div>
      {sectionOpen && (<>
      <p className="text-[11px] text-muted mb-3">
        {forCreative
          ? 'Ticked here overrides the clinic for this creative only. Leave a row untouched and the clinic rule applies.'
          : 'The standing rule for this clinic: the work steps land on these people automatically, and only the named reviewers can sign off. Tick nobody in a row and the step is assigned by hand, or anyone who qualifies can review.'}
      </p>
      {!forCreative && (
        <label className={cn('flex items-start gap-2 mt-3 text-sm', isAdmin ? 'cursor-pointer' : 'opacity-70')} data-testid="final-with-ad">
          <input type="checkbox" className="mt-1 accent-sky-500" disabled={!isAdmin || finalQ.isPending}
            checked={!!finalWithAd} onChange={(e) => finalQ.mutate(e.target.checked)} />
          <span>
            <span className="text-muted-strong">Final review also needs the art director</span>
            <span className="block text-[11px] text-muted">Off: the final edit is the content creator's decision, and it is done once they approve. The art director always decides the rough cut and colouring.</span>
          </span>
        </label>
      )}
      {!forCreative && (
        <label className={cn('flex items-start gap-2 mt-2 text-sm', canPlan ? 'cursor-pointer' : 'opacity-70')} data-testid="client-signoff">
          <input type="checkbox" className="mt-1 accent-sky-500" disabled={!canPlan || signoffQ.isPending}
            checked={doctorSignoff} onChange={(e) => signoffQ.mutate(e.target.checked)} />
          <span>
            <span className="text-muted-strong">Client sign-off by the piece's own doctor</span>
            <span className="block text-[11px] text-muted">On: each piece goes to the portal login of its own doctor, who sees and signs her pieces only; several doctors each sign their own. A piece with no doctor, or a doctor with no login, goes to the contacts named below. A clinic-wide contact can still sign in her place. Off: the contacts named below, or every contact.</span>
          </span>
        </label>
      )}
      {[['Who does the work', work], ['Who signs it off', review]].map(([title, list]) => (list as ApproverRouting[]).length === 0 ? null : (
      <div key={title as string}>
      <div className="text-[10px] uppercase tracking-wide text-muted mt-3 mb-1">{title as string}</div>
      <div className="divide-y divide-slate-800/60">
        {(list as ApproverRouting[]).map((r: ApproverRouting, i: number, arr: ApproverRouting[]) => (
          <div key={r.role} className="py-2.5" data-testid={`routing-${r.role}`}>
            {r.kind === 'work' && (i === 0 || arr[i - 1].group !== r.group) && (
              <div className="text-[10px] text-muted/80 mb-1.5">{GROUP_LABEL[r.group ?? 'shared'] ?? ''}</div>
            )}
            <div className="flex flex-wrap items-baseline gap-2 mb-1.5">
              <span className="text-sm text-muted-strong">{r.label}</span>
              <span className={cn('text-[10px] px-1.5 py-0.5 rounded-full',
                r.inherited_from === 'creative' ? 'bg-accent-600/20 text-accent-400'
                  : r.inherited_from === 'clinic' ? 'bg-slate-800 text-muted' : 'bg-slate-800/60 text-muted/80')}>
                {r.inherited_from === 'creative' ? 'set on this creative' : r.inherited_from === 'clinic' ? 'from the clinic' : 'anyone who qualifies'}
              </span>
              <span className="text-[11px] text-muted ml-auto">
                {r.effective_names.length ? `Today: ${r.effective_names.join(', ')}` : 'Nobody available yet'}
              </span>
            </div>
            <div className="flex flex-wrap gap-2">
              {r.candidates.map((c) => {
                const on = current(r.role).includes(c.id);
                return (
                  <button key={c.id} type="button" disabled={locked(r)} onClick={() => toggle(r.role, c.id)}
                    className={cn('text-[11px] px-3 py-1.5 min-h-[32px] rounded-full border transition',
                      on ? 'border-accent-400 bg-accent-600/20 text-accent-400' : 'border-slate-800 text-muted hover:text-muted-strong',
                      locked(r) && 'cursor-default opacity-70')}>
                    {c.display_name}
                    {c.kind === 'staff' && c.designations.length > 0 && <span className="opacity-60"> · {c.designations[0].replace(/_/g, ' ')}</span>}
                  </button>
                );
              })}
              {r.candidates.length === 0 && (
                <span className="text-[11px] text-amber-300">
                  {r.role === 'client' ? 'No portal contact on this clinic yet.' : 'Nobody on this clinic carries that designation yet.'}
                </span>
              )}
            </div>
          </div>
        ))}
      </div>
      </div>
      ))}
      </>)}
    </Card>
  );
}
